
// Vue
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin";
import { Component, Vue, Mixins, Watch, Prop, Ref } from "vue-property-decorator";
import { namespace, Getter, State } from "vuex-class";

// Services
import api from "@/services/api.service";

// Third Party
import moment from "moment";
import { formatDateMixin } from "@/mixins";
import { get } from "lodash";

// Types
import { FilteredEvent, SystemEventRecord, CallQueue, PickupCall, CallClient } from "@/store/eventqueue/types";
import {  VueBootstrapField, UserPermissions, User } from "@/store/types";

// Components
import LinkedTaskIcon from "@/components/alarm-queue/LinkedTaskIcon.vue";
import IntercomModal from "@/components/event-queue/IntercomModal.vue";

// Namespaces
const SessionStore = namespace("sessions");
const Eventqueue = namespace("eventqueue");
const Areas = namespace("areas");

const { convertUtcDateTimeToLocalDateTimeString } = formatDateMixin.methods;

@Component({
	components: {		
		"intercom-preview": IntercomModal,
		"linked-task-icon": LinkedTaskIcon
	}
})

export default class Intercom extends Mixins(RegionalHandlerMixin) {
	
	// Vuex
	@State(state => state.User) user: User;

	@Eventqueue.State private callQueue: CallQueue[];
	@Eventqueue.State private callClients: CallClient[];
	@Eventqueue.State private showIntercomModal: boolean;
	@Eventqueue.Mutation private setShowIntercomModal: (visible: boolean) => void;

	@Areas.Action getAreaList: () => Promise<void>;
	@Areas.Getter private getAreaTitleById: (groupId: number) => string;
	@Getter private getPermissions: UserPermissions;

	// Props	
	@Prop({ type: Boolean, default: false }) private isPlugin: boolean;

	@SessionStore.Action updateSession: ({ resourceId, eventId }) => Promise<void>;

	// Properties
	private collapseState: boolean = false;
	private ActiveCall = null;

	private selectedCallTemplate : PickupCall = {
		userId: 0,
		callId: 0,
		eventId: 0,
		areaName: null,
		deviceId: 0,
		deviceEndPoint: null,
		title: null
	};

	private selectedCall: PickupCall = { ...this.selectedCallTemplate };

	private fields: VueBootstrapField[] = [
		{ key: "callId", label: "Call ID" },
		{ key: "groupId", label: "Area", formatter: (groupId, key, item) => {
				return this.getTitle(groupId);
			} 
		},
		{ key: "waiting", label: "Waiting" },
		{ key: 'actions', label: 'Actions' }
	]

	private async mounted() {
		this.collapseState = this.isPlugin;
		
		await this.getAreaList();		
	}

	private async beforeDestroyed() {
		if (this.selectedCall.callId == 0 || this.selectedCall.userId == 0) return;
		
		this.selectedCall = { ...this.selectedCallTemplate };
	}	

	/**
	 * Permission and feature check
	 */
	private get canViewSystemEvents(): boolean {
		return (
			get(this.featuresList, ["Alarms", "EventQueue", "SystemEvents"]) &&
			(this.getPermissions.isAdmin || this.getPermissions.canViewSystemEvents)
		);
	}

	public get showIntercomAlarmQueue(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Intercom", "DisplayInQueue"]);
	}

	private toggleMenu() {
		this.collapseState = !this.collapseState;
	}

	private get getAvaliableCalls() {
		return this.callQueue.filter((call: CallQueue) => call.clientId == null);
	}

	private get avaliableClients(): number {
		return this.callClients.filter(x => x.userId == null).length ?? 0;
	}

	private get activeCalls(): number {
		return this.callQueue.filter(x => x.pickedUp).length ?? 0;
	}

	private get OnActiveCall() {
		return this.ActiveCall != null;
	}

	private getTitle(groupId): string {

		let areaName = groupId > 0 ? this.getAreaTitleById(groupId) : null;

        return areaName && this.selectedCall.title 
		? `${areaName}: ${this.selectedCall.title}` 
		: "Incoming call...";
	}

	private getAreaTitle(groupId: number): string {
		return this.getAreaTitleById(groupId);
	}

	private async pickupCall(call: CallQueue) {
		
		const currentCaller: PickupCall = {
			userId: this.user.userId,
			callId: call.callId,
			eventId: call.eventId,
			areaName: this.getAreaTitleById(call.groupId),
			deviceId: call.deviceId,
			deviceEndPoint: call.deviceEndPoint,
			title: call.title
		}
		const res = await api.setCaller(currentCaller);
		if (res?.status == 200) {			
			this.selectedCall = currentCaller;
			this.setShowIntercomModal(true);
		} else {
			console.error(`Couldn't set caller for CallId: ${ call.callId }`);
		}
	}

	private escapedTime(time: Date) {
		let now = moment(new Date()); //todays date
		let end = convertUtcDateTimeToLocalDateTimeString(time); // another date
		let duration = moment.duration(now.diff(end));
		let seconds = duration.asSeconds();
		let minutes = duration.asMinutes();
		let hours = duration.asHours();

		let waitTime = seconds;
		let suffix = "s";

		// 1 min
		if (seconds > 60) {
			waitTime = minutes;
			suffix = "m";
		}
		// 1 hour
		if (minutes > 60) {
			waitTime = hours;
			suffix = "hrs";
		}

		return waitTime.toFixed(2).toString() + suffix;
	}
}
